import { COMMENT_THREADS_KEY, Y } from '@piccolohealth/pbs-common';
import { P } from '@piccolohealth/util';
import { Mark, mergeAttributes } from '@tiptap/core';
import { Slice } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { ySyncPluginKey, yUndoPluginKey } from 'y-prosemirror';
import { getMarksByName, mapMarks } from '../../utils';

export type CommentStatus = 'open' | 'resolved';

export interface CommentThread {
  id: string;
  comments: CommentData[];
  // Status is optional, due to legacy reason.
  // Some documents & templates may/do were created before the status field was added.
  // Could migrate the data, but for now we'll just leave it optional.
  status?: CommentStatus;
}

export interface Reaction {
  id: string;
  userId: string;
  username: string;
}

export interface CommentData {
  content: string;
  reactions?: Reaction[];
  time: number;
  userId: string;
  username: string;
  picture?: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      addCommentThread: (commentThread: CommentThread) => ReturnType;
      updateCommentThread: (commentThread: CommentThread) => ReturnType;
      removeCommentThread: (commentThread: CommentThread) => ReturnType;
      resolveCommentThread: (commentThread: CommentThread) => ReturnType;
      unresolveCommentThread: (commentThread: CommentThread) => ReturnType;
    };
  }
}

export interface CommentOptions {
  document: Y.Doc;
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Comment mark - a mark that represents a comment thread
 * We store comment data in a Yjs document
 * so that it can be shared across multiple editors.
 * This is necessary for collaborative editing.
 *
 * The document id into the yjs doc is 'commentThreads'. The comment
 * mark stores the comment thread id in the comment attribute
 */

export const Comment = Mark.create<CommentOptions>({
  name: 'comment',
  spanning: false,
  inclusive: false,

  addOptions() {
    return {
      document: new Y.Doc(),
      HTMLAttributes: {},
    };
  },

  addStorage() {
    return {
      document: this.options.document,
    };
  },

  // Make the UndoManager aware of the commentThreads Y.Map, so we can actually undo/redo comment threads.
  onCreate() {
    const undoManager = yUndoPluginKey.getState(this.editor.state)?.undoManager;
    undoManager?.addToScope([this.options.document.getMap(COMMENT_THREADS_KEY)]);
  },

  // On the local user's own edits, remove any zombie threads.
  // Zombie threads = threads whose mark no longer exists in the doc.
  onTransaction({ transaction }) {
    // Skip any hocus transactions, since we only want to reconcile local edits.
    if (transaction.getMeta(ySyncPluginKey)?.isChangeOrigin) {
      return;
    }

    // If the document didn't actually change skip (i.e. not like highlighting, focusing etc)
    if (!transaction.docChanged) {
      return;
    }

    const commentThreadsMap = this.options.document.getMap(COMMENT_THREADS_KEY);
    const commentThreadIds = Array.from(commentThreadsMap.keys());
    const markedCommentThreadIds = new Set(
      getMarksByName(transaction, this.name).map(({ mark }) => mark.attrs.comment as string),
    );
    // Threads whose mark no longer exists in the doc
    const zombieCommentThreadIds = commentThreadIds.filter((id) => !markedCommentThreadIds.has(id));

    if (P.isEmpty(zombieCommentThreadIds)) {
      return;
    }

    this.options.document.transact(() => {
      zombieCommentThreadIds.forEach((id) => {
        commentThreadsMap.delete(id);
      });
    }, ySyncPluginKey);
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-comment]',
        getAttrs: (el) => !!(el as HTMLSpanElement).getAttribute('data-comment')?.trim() && null,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addAttributes() {
    return {
      comment: {
        default: null,
        parseHTML: (el) => (el as HTMLSpanElement).getAttribute('data-comment'),
        renderHTML: (attrs) => ({ 'data-comment': attrs.comment }),
      },
      status: {
        default: 'open',
        parseHTML: (el) => (el as HTMLSpanElement).getAttribute('data-status'),
        renderHTML: (attrs) => ({ 'data-status': attrs.status }),
      },
      payload: {
        default: null,
        parseHTML: (el) => {
          const raw = (el as HTMLSpanElement).getAttribute('data-comment-payload');
          return raw ? JSON.parse(raw) : null;
        },
        renderHTML: (attrs) =>
          attrs.payload ? { 'data-comment-payload': JSON.stringify(attrs.payload) } : {},
      },
    };
  },

  // Comment marks and their threads live in two places — the mark in the text,
  // the thread in the Yjs map. The clipboard only carries the mark, so a
  // cut/copy would lose the thread (and a cut deletes it from the map outright,
  // via the onTransaction above). So transformCopied packages the thread payload on
  // the mark, with the source doc id.
  //
  // transformPasted restores it on a same-doc paste but drops the mark on a
  // foreign-doc paste (comments are doc-scoped — they don't travel between docs).
  addProseMirrorPlugins() {
    const yDoc = this.options.document;
    const pluginName = this.name;

    return [
      new Plugin({
        props: {
          // On copy/cut, package each comment mark with its thread + the source
          // doc id, so the paste has everything it needs to restore the thread
          // even after a cut has removed the thread from the map.
          transformCopied(slice) {
            const commentThreadsMap = yDoc.getMap(COMMENT_THREADS_KEY);

            const content = mapMarks(slice.content, (marks) =>
              marks.map((m) => {
                if (m.type.name !== pluginName) {
                  return m;
                }

                const thread = commentThreadsMap.get(m.attrs.comment);

                if (!thread) {
                  return m;
                }

                return m.type.create({
                  comment: m.attrs.comment,
                  status: m.attrs.status,
                  payload: { docId: yDoc.guid, thread },
                });
              }),
            );

            return new Slice(content, slice.openStart, slice.openEnd);
          },

          // On paste, resolve each comment mark against its payload and source doc:
          // 1. Same-doc paste — restore the thread to the Yjs map, keep the mark, strip the payload.
          // 2. Foreign-doc paste — drop the mark (comments don't travel between documents).
          // 3. Mark without the required payload info — keep only if we already hold its thread, else drop.
          transformPasted(slice) {
            const commentThreadsMap = yDoc.getMap(COMMENT_THREADS_KEY);
            const threadIds = new Set(commentThreadsMap.keys());
            const commentThreadsToRecreate = new Map<string, CommentThread>();

            const content = mapMarks(slice.content, (marks) =>
              marks.flatMap((m) => {
                if (m.type.name !== pluginName) {
                  return [m];
                }
                const attrs = m.attrs as {
                  comment: string;
                  status: CommentStatus;
                  payload: { docId: string; thread: CommentThread } | null;
                };

                // Same-doc paste — restore the thread and keep the mark, but
                // strip the payload so it never lands in the live document.
                if (attrs.payload?.docId === yDoc.guid) {
                  commentThreadsToRecreate.set(attrs.comment, attrs.payload.thread);
                  return [m.type.create({ comment: attrs.comment, status: attrs.status })];
                }

                // Foreign-doc paste — drop the comment mark entirely.
                if (attrs.payload) {
                  return [];
                }

                // No payload (external source): keep only if we already know it.
                return threadIds.has(attrs.comment) ? [m] : [];
              }),
            );

            if (commentThreadsToRecreate.size > 0) {
              yDoc.transact(() => {
                for (const [id, thread] of commentThreadsToRecreate) {
                  commentThreadsMap.set(id, thread);
                }
              }, ySyncPluginKey);
            }

            return new Slice(content, slice.openStart, slice.openEnd);
          },
        },
      }),
    ];
  },

  addCommands() {
    // Wrap each command's map + PM mutation in one doc.transact so a single Ctrl+Z reverts both.
    return {
      addCommentThread:
        (commentThread: CommentThread) =>
        ({ commands }) => {
          if (this.storage.document.getMap(COMMENT_THREADS_KEY).has(commentThread.id)) {
            return false;
          }

          return this.storage.document.transact(() => {
            this.storage.document.getMap(COMMENT_THREADS_KEY).set(commentThread.id, {
              ...commentThread,
              status: 'open',
            });
            return commands.setMark(this.name, { comment: commentThread.id });
          }, ySyncPluginKey);
        },

      updateCommentThread:
        (updatedComment: CommentThread) =>
        ({ chain }) => {
          return this.storage.document.transact(() => {
            this.storage.document
              .getMap(COMMENT_THREADS_KEY)
              .set(updatedComment.id, updatedComment);
            return chain()
              .extendMarkRange(this.name)
              .updateAttributes(this.name, { comment: updatedComment.id })
              .run();
          }, ySyncPluginKey);
        },

      removeCommentThread:
        (commentThread: CommentThread) =>
        ({ tr }) => {
          return this.storage.document.transact(() => {
            this.storage.document.getMap(COMMENT_THREADS_KEY).delete(commentThread.id);

            // Find all comment marks with the same comment id
            const commentMarks = getMarksByName(tr, this.name, {
              comment: commentThread.id,
            });

            // Remove all comment marks
            commentMarks.forEach(({ mark, range }) => {
              tr.removeMark(range.from, range.to, mark);
            });

            return true;
          }, ySyncPluginKey);
        },

      resolveCommentThread:
        (commentThread: CommentThread) =>
        ({ tr }) => {
          return this.storage.document.transact(() => {
            // Find all open comment marks with the same comment id
            const commentMarks = getMarksByName(tr, this.name, {
              comment: commentThread.id,
              status: 'open',
            });

            // Remove all open comment marks and then add new comment
            // marks, with same attributes, but resolved status
            commentMarks.forEach(({ mark, range }) => {
              tr.removeMark(range.from, range.to, mark);
              tr.addMark(
                range.from,
                range.to,
                mark.type.create({ comment: commentThread.id, status: 'resolved' }),
              );
            });

            // Set the comment thread status to resolved
            this.storage.document.getMap(COMMENT_THREADS_KEY).set(commentThread.id, {
              ...commentThread,
              status: 'resolved',
            });

            return true;
          }, ySyncPluginKey);
        },

      unresolveCommentThread:
        (commentThread: CommentThread) =>
        ({ tr }) => {
          return this.storage.document.transact(() => {
            // Find all resolved comment marks with the same comment id
            const commentMarks = getMarksByName(tr, this.name, {
              comment: commentThread.id,
              status: 'resolved',
            });

            // Remove all resolved comment marks and then add new comment
            // marks, with same attributes, but open status
            commentMarks.forEach(({ mark, range }) => {
              tr.removeMark(range.from, range.to, mark);
              tr.addMark(
                range.from,
                range.to,
                mark.type.create({ comment: commentThread.id, status: 'open' }),
              );
            });

            // Set the comment thread status to open
            this.storage.document.getMap(COMMENT_THREADS_KEY).set(commentThread.id, {
              ...commentThread,
              status: 'open',
            });

            return true;
          }, ySyncPluginKey);
        },
    };
  },
});

export const getCommentThread = (document: Y.Doc, id: string): CommentThread | null => {
  return (document.getMap(COMMENT_THREADS_KEY).get(id) as CommentThread | undefined) ?? null;
};

export const getCommentThreads = (document: Y.Doc): CommentThread[] => {
  return Array.from(document.getMap(COMMENT_THREADS_KEY).values()) as CommentThread[];
};
